import React from "react";
import { Badge, Button, DatePicker, Table, Plus, Trash04, Edit01, Check, CalendarDate } from "@opendate-app/setlist";
import { useSeriesEditContext } from "../SeriesEditApp";
import StepPanel from "./StepPanel";
import StepFooter from "./StepFooter";
import TimeField from "./TimeField";
import PublishedLock, { PublishedLockNote } from "./PublishedLock";
import {
  WEEKDAYS,
  INTERVAL_OPTIONS,
  MAX_GENERATED_DATES,
  blankShow,
  clashingShowIds,
  countDatesInRange,
  datesInRange,
  endTimeOf,
  endsNextDay,
  entrySlots,
  withShowTime,
  formatDateLabel,
  formatRangeLabel,
  formatTimeLabel,
  generateMultipleShows,
  generateTimedEntry,
  sortTimesForDate,
  weekdayOf,
  withSeatingMode,
} from "../scheduleBuilder";

const MODES = [
  {
    value: "multiple_shows",
    label: "Multiple Shows",
    description: "Each time slot is a separate event with its own ticketing. Like a matinee + evening show.",
    seating: "Seated or general admission",
  },
  {
    value: "timed_entry",
    label: "Timed Entry",
    description: "One event per date with multiple entry time slots. Buyers pick a date, then an entry time.",
    seating: "General admission only",
  },
];

const isoToDate = (iso) => (iso ? new Date(`${iso}T12:00:00`) : null);
const dateToIso = (date) => {
  if (!date) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const ScheduleSection = () => {
  const { schedule, setSchedule, setTickets, goToStep, tracksOwnValues } = useSeriesEditContext();
  const { mode, startDate, endDate, daysOfWeek, showTimes, entry, generated } = schedule;
  const [editingRowId, setEditingRowId] = React.useState(null);

  const patch = (changes) => setSchedule((current) => ({ ...current, ...changes }));

  const rangeComplete = !!startDate && !!endDate && daysOfWeek.length > 0;
  const matchedDateCount = rangeComplete ? countDatesInRange(startDate, endDate, daysOfWeek) : 0;
  const rangeTooLong = matchedDateCount > MAX_GENERATED_DATES;
  const matchedDates = rangeComplete && !rangeTooLong ? datesInRange(startDate, endDate, daysOfWeek) : [];
  const slots = entrySlots(entry);
  const selectedDays = WEEKDAYS.filter((day) => daysOfWeek.includes(day.value));

  const clashesByDay = {};
  selectedDays.forEach((day) => {
    clashesByDay[day.value] = clashingShowIds(showTimes[day.value] || []);
  });
  const hasClash = Object.values(clashesByDay).some((ids) => ids.size > 0);

  const showTimesComplete =
    rangeComplete && selectedDays.some((day) => (showTimes[day.value] || []).length > 0);
  const entryComplete = rangeComplete && slots.length > 0 && Number(entry.capacity) > 0;
  const detailsComplete =
    !rangeTooLong && (mode === "multiple_shows" ? showTimesComplete && !hasClash : entryComplete);

  const toggleDay = (value) => {
    const next = daysOfWeek.includes(value)
      ? daysOfWeek.filter((day) => day !== value)
      : [...daysOfWeek, value].sort((a, b) => a - b);
    const nextShowTimes = { ...showTimes };
    if (!nextShowTimes[value]) nextShowTimes[value] = [blankShow()];
    patch({ daysOfWeek: next, showTimes: nextShowTimes, generated: [] });
  };

  const setShowsForDay = (day, shows) =>
    patch({ showTimes: { ...showTimes, [day]: shows }, generated: [] });

  const updateShow = (day, showId, changes) =>
    setShowsForDay(
      day,
      (showTimes[day] || []).map((show) => (show.id === showId ? { ...show, ...changes } : show)),
    );

  const generate = () => {
    const rows =
      mode === "multiple_shows"
        ? generateMultipleShows({ startDate, endDate, daysOfWeek, showTimes })
        : generateTimedEntry({ startDate, endDate, daysOfWeek, entry });
    patch({ generated: rows });
    setEditingRowId(null);
  };

  const updateRow = (rowId, changes) =>
    patch({ generated: generated.map((row) => (row.id === rowId ? { ...row, ...changes } : row)) });

  const removeRow = (rowId) => patch({ generated: generated.filter((row) => row.id !== rowId) });

  const addRow = () => {
    const lastDate = generated.length ? generated[generated.length - 1].date : startDate;
    const row =
      mode === "multiple_shows"
        ? { id: `series-schedule-row-added-${Date.now()}`, date: lastDate, ...blankShow() }
        : { id: `series-schedule-row-added-${Date.now()}`, date: lastDate, times: [...slots] };
    patch({ generated: [...generated, row] });
    setEditingRowId(row.id);
  };

  const rangeSummary = rangeComplete
    ? `${formatRangeLabel(startDate, endDate)} · ${daysOfWeek.length} days/wk`
    : "";

  const multipleShowsColumns = [
    {
      key: "date",
      header: "Date",
      width: "180px",
      render: (row) =>
        editingRowId === row.id ? (
          <input
            type="date"
            className="series-edit-input series-inline-input"
            value={row.date}
            onChange={(event) => updateRow(row.id, { date: event.target.value })}
          />
        ) : (
          formatDateLabel(row.date)
        ),
    },
    {
      key: "doors",
      header: "Doors",
      width: "110px",
      render: (row) =>
        editingRowId === row.id ? (
          <TimeField
            className="series-inline-input"
            ariaLabel="Doors"
            value={row.doors}
            onChange={(next) => updateRow(row.id, { doors: next })}
          />
        ) : (
          formatTimeLabel(row.doors)
        ),
    },
    {
      key: "showTime",
      header: "Show",
      width: "110px",
      render: (row) =>
        editingRowId === row.id ? (
          <TimeField
            className="series-inline-input"
            ariaLabel="Show time"
            value={row.showTime}
            onChange={(next) => updateRow(row.id, withShowTime(row, next))}
          />
        ) : (
          formatTimeLabel(row.showTime)
        ),
    },
    {
      key: "endTime",
      header: "Ends",
      width: "120px",
      render: (row) =>
        editingRowId === row.id ? (
          <TimeField
            className="series-inline-input"
            ariaLabel="Ends"
            value={endTimeOf(row)}
            onChange={(next) => updateRow(row.id, { endTime: next })}
          />
        ) : (
          <>
            {formatTimeLabel(endTimeOf(row))}
            {endsNextDay(row.showTime, endTimeOf(row)) && <span className="series-next-day-suffix"> +1</span>}
          </>
        ),
    },
    {
      key: "label",
      header: "Label",
      render: (row) =>
        editingRowId === row.id ? (
          <input
            className="series-edit-input series-inline-input"
            value={row.label}
            onChange={(event) => updateRow(row.id, { label: event.target.value })}
          />
        ) : (
          row.label
        ),
    },
  ];

  const timedEntryColumns = [
    {
      key: "date",
      header: "Date",
      width: "180px",
      render: (row) =>
        editingRowId === row.id ? (
          <input
            type="date"
            className="series-edit-input series-inline-input"
            value={row.date}
            onChange={(event) => updateRow(row.id, { date: event.target.value })}
          />
        ) : (
          formatDateLabel(row.date)
        ),
    },
    {
      key: "times",
      header: "Entry Times",
      render: (row) => (
        <div className="series-entry-chips">
          {row.times.map((time) => (
            <span key={time} className="series-entry-chip">
              {formatTimeLabel(time)}
              {editingRowId === row.id && (
                <button
                  type="button"
                  className="series-entry-chip-remove"
                  aria-label={`Remove ${formatTimeLabel(time)}`}
                  onClick={() => updateRow(row.id, { times: row.times.filter((value) => value !== time) })}
                >
                  <i className="fal fa-times" aria-hidden="true" />
                </button>
              )}
            </span>
          ))}
          {editingRowId === row.id && (
            <TimeField
              className="series-inline-input series-entry-chip-input"
              ariaLabel="Add an entry time"
              placeholder="Add time"
              value=""
              onChange={(next, picker) => {
                if (!next) return;
                picker?.clear();
                if (row.times.includes(next)) return;
                updateRow(row.id, { times: sortTimesForDate([...row.times, next], schedule.entry) });
              }}
            />
          )}
        </div>
      ),
    },
  ];

  const actionsColumn = {
    key: "actions",
    header: "",
    align: "right",
    width: "96px",
    render: (row) => (
      <div className="series-row-actions">
        <Button
          variant="tertiary"
          size="sm"
          icon={editingRowId === row.id ? Check : Edit01}
          aria-label={editingRowId === row.id ? "Done editing" : "Edit row"}
          onClick={() => setEditingRowId(editingRowId === row.id ? null : row.id)}
        />
        <Button
          variant="tertiary"
          size="sm"
          icon={Trash04}
          aria-label="Remove row"
          onClick={() => removeRow(row.id)}
        />
      </div>
    ),
  };

  const distinctLabels = [...new Set(generated.map((row) => row.label).filter(Boolean))];
  const totalSlots = generated.reduce((sum, row) => sum + (row.times ? row.times.length : 0), 0);

  return (
    <div className="series-edit-card">
      <h2 className="series-edit-section-title">Schedule</h2>
      <p className="series-edit-section-subtitle">Define when each performance takes place.</p>
      {tracksOwnValues && <PublishedLockNote />}

      <PublishedLock locked={!!tracksOwnValues}>
        <span className="series-edit-label series-edit-label-flush">Scheduling Mode</span>
        <div className="series-mode-grid" role="radiogroup" aria-label="Scheduling mode">
          {MODES.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={mode === option.value}
              className={["series-mode-option", mode === option.value ? "selected" : ""].join(" ").trim()}
              onClick={() => {
                patch({ mode: option.value, generated: [] });
                if (option.value === "timed_entry") {
                  setTickets((current) =>
                    current.seatingMode === "seated" ? withSeatingMode(current, "general_admission") : current,
                  );
                }
              }}
            >
              <span className="series-mode-option-head">
                <span className="series-mode-radio" aria-hidden="true" />
                <span className="series-mode-option-label">{option.label}</span>
              </span>
              <span className="series-mode-option-description">{option.description}</span>
              <span className="series-mode-option-seating">
                <Badge>{option.seating}</Badge>
              </span>
            </button>
          ))}
        </div>

        <StepPanel number={1} title="Date Range" complete={rangeComplete && !rangeTooLong} summary={rangeSummary}>
          <div className="series-edit-row">
            <DatePicker
              label="Start Date"
              value={isoToDate(startDate)}
              onChange={(date) => patch({ startDate: dateToIso(date), generated: [] })}
            />
            <DatePicker
              label="End Date"
              value={isoToDate(endDate)}
              min={isoToDate(startDate) || undefined}
              onChange={(date) => patch({ endDate: dateToIso(date), generated: [] })}
            />
          </div>

          <span className="series-edit-label">Repeat On</span>
          <div className="series-weekday-row">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                aria-pressed={daysOfWeek.includes(day.value)}
                className={["series-weekday", daysOfWeek.includes(day.value) ? "selected" : ""].join(" ").trim()}
                onClick={() => toggleDay(day.value)}
              >
                {day.short}
              </button>
            ))}
          </div>
        </StepPanel>

        {mode === "multiple_shows" ? (
          <StepPanel
            number={2}
            title="Show Times"
            complete={showTimesComplete}
            locked={!rangeComplete}
            lockedHint="Complete date range first"
          >
            {selectedDays.map((day) => {
              const shows = showTimes[day.value] || [];
              const clashes = clashesByDay[day.value];
              return (
                <div key={day.value} className="series-day-group">
                  <div className="series-day-group-header">
                    <span className="series-day-group-name">{day.long}</span>
                    <span className="series-day-group-count">
                      {shows.length} {shows.length === 1 ? "show" : "shows"}
                    </span>
                  </div>
                  <div className="series-day-group-body">
                    {shows.map((show) => (
                      <div
                        key={show.id}
                        className={["series-show-row", clashes.has(show.id) ? "clashing" : ""]
                          .join(" ")
                          .trim()}
                      >
                        <label className="series-show-field">
                          <span className="series-edit-label series-edit-label-flush">Label</span>
                          <input
                            className="series-edit-input"
                            value={show.label}
                            onChange={(event) => updateShow(day.value, show.id, { label: event.target.value })}
                          />
                        </label>
                        <label className="series-show-field">
                          <span className="series-edit-label series-edit-label-flush">Doors</span>
                          <TimeField
                            ariaLabel="Doors"
                            value={show.doors}
                            onChange={(next) => updateShow(day.value, show.id, { doors: next })}
                          />
                        </label>
                        <label className="series-show-field">
                          <span className="series-edit-label series-edit-label-flush">Show Time</span>
                          <TimeField
                            ariaLabel="Show time"
                            invalid={clashes.has(show.id)}
                            value={show.showTime}
                            onChange={(next) => updateShow(day.value, show.id, withShowTime(show, next))}
                          />
                        </label>
                        <label className="series-show-field series-show-field-end">
                          <span className="series-edit-label series-edit-label-flush">Ends</span>
                          <TimeField
                            ariaLabel="Ends"
                            className={endsNextDay(show.showTime, endTimeOf(show)) ? "series-time-input-next-day" : ""}
                            value={endTimeOf(show)}
                            onChange={(next) => updateShow(day.value, show.id, { endTime: next })}
                          />
                          {endsNextDay(show.showTime, endTimeOf(show)) && (
                            <span className="series-next-day-badge">
                              <Badge size="sm" color="gray">
                                +1<span className="series-next-day-unit"> day</span>
                              </Badge>
                            </span>
                          )}
                        </label>
                        <div className="series-show-delete">
                          <Button
                            variant="tertiary"
                            size="sm"
                            icon={Trash04}
                            aria-label={`Remove ${show.label || "show"} on ${day.long}`}
                            disabled={shows.length === 1}
                            onClick={() =>
                              setShowsForDay(
                                day.value,
                                shows.filter((candidate) => candidate.id !== show.id),
                              )
                            }
                          />
                        </div>
                      </div>
                    ))}
                    <Button
                      variant="link-gray"
                      size="sm"
                      icon={Plus}
                      onClick={() => setShowsForDay(day.value, [...shows, blankShow(shows.length)])}
                    >
                      Add Show
                    </Button>
                  </div>
                </div>
              );
            })}
          </StepPanel>
        ) : (
          <StepPanel
            number={2}
            title="Entry Time Slots"
            complete={entryComplete}
            locked={!rangeComplete}
            lockedHint="Complete date range first"
          >
            <div className="series-entry-grid">
              <label className="series-show-field">
                <span className="series-edit-label series-edit-label-flush">First Entry</span>
                <TimeField
                  ariaLabel="First entry"
                  value={entry.firstEntry}
                  onChange={(next) => patch({ entry: { ...entry, firstEntry: next }, generated: [] })}
                />
              </label>
              <label className="series-show-field">
                <span className="series-edit-label series-edit-label-flush">Last Entry</span>
                <TimeField
                  ariaLabel="Last entry"
                  value={entry.lastEntry}
                  onChange={(next) => patch({ entry: { ...entry, lastEntry: next }, generated: [] })}
                />
              </label>
              <label className="series-show-field">
                <span className="series-edit-label series-edit-label-flush">Interval</span>
                <select
                  className="series-edit-input"
                  value={entry.intervalMinutes}
                  onChange={(event) =>
                    patch({ entry: { ...entry, intervalMinutes: Number(event.target.value) }, generated: [] })
                  }
                >
                  {INTERVAL_OPTIONS.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      Every {minutes} min
                    </option>
                  ))}
                </select>
              </label>
              <label className="series-show-field">
                <span className="series-edit-label series-edit-label-flush">Capacity / entry time</span>
                <input
                  type="number"
                  min="0"
                  className="series-edit-input"
                  value={entry.capacity}
                  onChange={(event) =>
                    patch({ entry: { ...entry, capacity: event.target.value }, generated: [] })
                  }
                />
              </label>
            </div>
            {slots.length > 0 && (
              <p className="series-inline-summary">
                {slots.length} entry {slots.length === 1 ? "slot" : "slots"} per date
                {Number(entry.capacity) > 0 &&
                  `, ${entry.capacity} guests each (${slots.length * Number(entry.capacity)}/day)`}
                : {slots.map(formatTimeLabel).join(", ")}
              </p>
            )}
          </StepPanel>
        )}

        {hasClash && mode === "multiple_shows" && (
          <p className="series-schedule-error" role="alert">
            Two shows on the same day can&rsquo;t start at the same time
          </p>
        )}

        {rangeTooLong && (
          <p className="series-schedule-error" role="alert">
            That range covers {matchedDateCount} dates &mdash; more than the {MAX_GENERATED_DATES} this
            builder can generate at once. Shorten the range or pick fewer days.
          </p>
        )}

        {rangeComplete && (
          <div className="series-generate-row">
            <Button variant="primary" icon={CalendarDate} disabled={!detailsComplete} onClick={generate}>
              Generate Schedule
            </Button>
          </div>
        )}

        {generated.length > 0 && (
          <div className="series-generated">
            <div className="series-generated-header">
              <h3 className="series-generated-title">Generated Schedule</h3>
              <div className="series-generated-actions">
                <Button variant="link-gray" size="sm" icon={Plus} onClick={addRow}>
                  Add Date
                </Button>
                <Button variant="link-gray" size="sm" onClick={() => patch({ generated: [] })}>
                  Clear All
                </Button>
              </div>
            </div>
            <div className="series-schedule-table">
              <Table
                columns={[...(mode === "multiple_shows" ? multipleShowsColumns : timedEntryColumns), actionsColumn]}
                data={generated}
                getRowKey={(row) => row.id}
              />
            </div>
            <p className="series-generated-summary">
              {mode === "multiple_shows" ? (
                <>
                  <strong>{generated.length}</strong> individual shows across{" "}
                  <strong>{distinctLabels.length}</strong> show{" "}
                  {distinctLabels.length === 1 ? "type" : "types"}
                  {distinctLabels.length > 0 && ` (${distinctLabels.join(", ")})`}. Each type can have its own
                  ticket configuration.
                </>
              ) : (
                <>
                  <strong>{generated.length}</strong> dates with <strong>{totalSlots}</strong> total entry slots.
                  Each date creates 1 event on the calendar. Configure ticket types in the next step.
                </>
              )}
            </p>
          </div>
        )}

      </PublishedLock>

      <StepFooter
        onBack={() => goToStep("details")}
        onNext={() => goToStep("tickets")}
        nextDisabled={generated.length === 0}
      />
      {rangeComplete && !rangeTooLong && matchedDates.length === 0 && (
        <p className="series-inline-summary">No dates in this range fall on the selected days.</p>
      )}
    </div>
  );
};

export default ScheduleSection;
